import { createTheme, Theme } from '@mui/material/styles';

// Define color palettes for light and dark themes
const lightPalette = {
  mode: 'light',
  primary: {
    main: '#1976d2',
    light: '#42a5f5',
    dark: '#1565c0',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#dc004e',
    light: '#ff5983',
    dark: '#9a0036',
    contrastText: '#ffffff',
  },
  background: {
    default: '#f5f5f5',
    paper: '#ffffff',
  },
  text: {
    primary: '#212121',
    secondary: '#757575',
  },
  divider: '#e0e0e0',
};

const darkPalette = {
  mode: 'dark',
  primary: {
    main: '#42a5f5',
    light: '#e3f2fd',
    dark: '#90caf9',
    contrastText: '#000000',
  },
  secondary: {
    main: '#bf5f82',
    light: '#ffc1e3',
    dark: '#bf5f82',
    contrastText: '#000000',
  },
  background: {
    default: '#1E1E32',
    paper: 'rgba(30, 30, 50, 0.6)',
  },
  text: {
    primary: '#ffffff',
    secondary: '#b0b0b0',
  },
  divider: '#424242',
};

// Common theme options
const commonThemeOptions = {
  typography: {
    fontFamily: '"Unilever Desire", sans-serif',
    h1: {
      fontSize: '40px',
      fontWeight: 600,
    },
    h2: {
      fontSize: '32px',
      fontWeight: 600,
    },
    h3: {
      fontSize: '28px',
      fontWeight: 600,
    },
    h4: {
      fontSize: '24px',
      fontWeight: 600,
    },
    h5: {
      fontSize: '20px',
      fontWeight: 600,
    },
    h6: {
      fontSize: '16px',
      fontWeight: 600,
    },
    body1: {
      fontSize: '16px',
      fontWeight: 400,
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
    },
    subtitle1: {
      fontSize: '15px',
      fontWeight: 400,
    },
    subtitle2: {
      fontSize: '13px',
      fontWeight: 400,
    },
    caption: {
      fontSize: '12px',
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
  },
};

// Light theme specific component overrides
const lightThemeComponents = {
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: '#1976d2',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    },
  },
};

// Create light theme
export const lightTheme = createTheme({
  palette: lightPalette,
  ...commonThemeOptions,
  components: {
    ...commonThemeOptions.components,
    ...lightThemeComponents,
  },
});

// Create dark theme
export const darkTheme = createTheme({
  palette: darkPalette,
  ...commonThemeOptions,
});

// Theme modes enum
export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
} as const;

export type ThemeMode = typeof THEME_MODES[keyof typeof THEME_MODES];

// Function to get theme by mode
export const getTheme = (mode: ThemeMode): Theme => {
  return mode === THEME_MODES.DARK ? darkTheme : lightTheme;
};

export default { lightTheme, darkTheme, getTheme, THEME_MODES };